'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Message } from './cards-grid.elements';

const target = {
  education: 'edukacija',
  gift: 'edukacija',
  post: 'objava',
};

const CARDS_BLOCK_SIZE = 20;

const CardsGrid = ({ data, Card, withSearch, filters = [], type, subType, user }) => {
  const [visibleItems, setVisibleItems] = useState(CARDS_BLOCK_SIZE);
  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);
  const timeoutRef = useRef(null);
  const loadingRef = useRef(false);
  const dataLengthRef = useRef(data.length);

  dataLengthRef.current = data.length;

  // Reset visible items when data changes significantly
  useEffect(() => {
    setVisibleItems(CARDS_BLOCK_SIZE);
  }, [data]);

  const handleIntersection = useCallback((entries) => {
    if (entries[0].isIntersecting && !loadingRef.current) {
      loadingRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setVisibleItems((prev) => Math.min(prev + CARDS_BLOCK_SIZE, dataLengthRef.current));
        loadingRef.current = false;
      }, 300);
    }
  }, []);

  useEffect(() => {
    const currentRef = loadMoreRef.current;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!currentRef) return;

    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin: '100px',
    });

    observerRef.current.observe(currentRef);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [handleIntersection]);

  return (
    <Container>
      {data.length > 0 ? (
        data
          .slice(0, visibleItems) // Only render the visible items
          .filter((item, i, self) => i === self.findIndex((t) => t.id === item.id))
          .map((item) => (
            <Card key={item.id} data={item} type={type} subType={subType} user={user} />
          ))
      ) : (
        <Message $isVisible>
          {withSearch && filters.length > 0 ? (
            <>
              <p>Žao nam je, nema rezultata za tu pretragu.</p>
              <p>Molimo pokušajte s drugim ključnim riječima.</p>
            </>
          ) : (
            <p>Žao nam je, nema {target[type]} za prikazati.</p>
          )}
        </Message>
      )}

      {data.length > visibleItems && (
        <Message $isVisible ref={loadMoreRef}>
          <span className="loading" aria-label="Loading" />
        </Message>
      )}
    </Container>
  );
};

export { CardsGrid };
